import { useState, useEffect } from 'react';
import { Plus, X, CheckCircle, BarChart3, Calendar, Users, Clock, AlertCircle, Trash2, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import { votingAPI } from '../services/api';

interface Poll {
  id: string;
  title: string;
  description: string;
  options: { id: string; text: string; votes: number }[];
  status: 'active' | 'closed';
  hasVoted?: boolean;
  selectedOptionId?: string;
  createdAt: string;
  endDate?: string;
  totalVotes: number;
  createdBy: string;
}

export default function Voting() {
  const { user, isAdmin } = useAuth();
  const { success, error } = useToast();
  const [activeTab, setActiveTab] = useState<'active' | 'closed'>('active');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedPoll, setSelectedPoll] = useState<Poll | null>(null);
  const [loading, setLoading] = useState(true);
  const [polls, setPolls] = useState<Poll[]>([]);
  const [votingInProgress, setVotingInProgress] = useState<string | null>(null);
  // Track which option the user voted for, per poll (local state since backend
  // doesn't return per-user hasVoted in the list endpoint).
  const [userVotes, setUserVotes] = useState<Record<string, string>>({});
  // Track which polls the user is currently changing their vote on.
  const [changingVote, setChangingVote] = useState<Set<string>>(new Set());
  const [newPoll, setNewPoll] = useState({
    title: '',
    description: '',
    // BUG-C (#313, QA bugfix-cycle-1004-0400): the backend POST /voting/condo/:id
    // requires start AND end (start must precede end). The form previously had
    // no start-date field and sent only endDate -> guaranteed 400 with no
    // visible feedback (silent failure; admin vote creation impossible).
    startDate: '',
    endDate: '',
    options: ['', '']
  });

  useEffect(() => {
    loadPolls();
  }, []);

  const loadPolls = async () => {
    setLoading(true);
    try {
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const condoId = currentUser.condoId;
      if (!condoId) {
        setPolls([]);
        return;
      }
      const response = await votingAPI.getAll(condoId);
      const data = response.data;
      const rawPolls = data?.data?.polls ?? data?.polls ?? [];
      const mapped: Poll[] = (Array.isArray(rawPolls) ? rawPolls : []).map((p: any) => {
        const totalVotes = (p.options || []).reduce((sum: number, o: any) => sum + (o.votes || 0), 0);
        const pollId = String(p.id);
        return {
          id: pollId,
          title: p.title || '',
          description: p.description || '',
          options: (p.options || []).map((o: any) => ({
            id: String(o.id),
            text: o.text || o.option_text || '',
            votes: o.votes || o.votes_count || 0
          })),
          status: (p.status === 'closed' ? 'closed' : 'active') as 'active' | 'closed',
          hasVoted: !!userVotes[pollId],
          selectedOptionId: userVotes[pollId],
          createdAt: p.startDate || p.createdAt || '',
          endDate: p.endDate || undefined,
          totalVotes,
          createdBy: p.createdBy ? String(p.createdBy) : 'Administración'
        };
      });
      setPolls(mapped);
    } catch (err) {
      console.error('Error loading polls:', err);
      setPolls([]);
      error('Error al cargar votaciones');
    } finally {
      setLoading(false);
    }
  };

  const filteredPolls = polls.filter(p => p.status === activeTab);

  const handleVote = async (pollId: string, optionId: string) => {
    if (votingInProgress) return;
    setVotingInProgress(pollId);
    try {
      // Backend expects numeric ids (string optionId returns 400 "Invalid option").
      // Coerce when the values are numeric; otherwise pass through.
      const pid: any = /^\d+$/.test(pollId) ? Number(pollId) : pollId;
      const oid: any = /^\d+$/.test(optionId) ? Number(optionId) : optionId;
      await votingAPI.vote(pid, oid);

      // Track the vote locally
      setUserVotes(prev => ({ ...prev, [pollId]: optionId }));
      // Remove from changingVote set if present
      setChangingVote(prev => {
        const next = new Set(prev);
        next.delete(pollId);
        return next;
      });

      success('Voto registrado exitosamente');

      // Re-fetch so counts come from the server
      await loadPolls();
    } catch (err: any) {
      console.error('Error voting:', err);
      const msg = err?.response?.data?.error || 'Error al registrar voto';
      error(msg);
    } finally {
      setVotingInProgress(null);
    }
  };

  const handleChangeVote = (pollId: string) => {
    // Enter "change vote" mode: clear local hasVoted state so options are shown again.
    // The backend doesn't support changing votes, so if the user submits a different
    // option, the backend will reject with "Already voted". We handle that gracefully.
    setChangingVote(prev => {
      const next = new Set(prev);
      next.add(pollId);
      return next;
    });
    // Temporarily clear the local vote tracking so the UI shows voting buttons
    setUserVotes(prev => {
      const next = { ...prev };
      delete next[pollId];
      return next;
    });
  };

  const handleCreatePoll = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const condoId = currentUser.condoId;
      if (!condoId) {
        error('No se pudo determinar el condominio');
        return;
      }
      const options = newPoll.options.filter(o => o.trim());
      if (options.length < 2) {
        error('Debes agregar al menos 2 opciones');
        return;
      }
      // BUG-C (#313): both dates are required by the API contract
      // (starts_at/ends_at; start must precede end). Validate client-side in
      // Spanish before hitting the endpoint.
      const startMs = Date.parse(newPoll.startDate);
      const endMs = Date.parse(newPoll.endDate);
      if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
        error('Debes indicar la fecha de inicio y la fecha de cierre');
        return;
      }
      if (startMs >= endMs) {
        error('La fecha de inicio debe ser anterior a la fecha de cierre');
        return;
      }
      await votingAPI.create(condoId, {
        title: newPoll.title,
        description: newPoll.description,
        starts_at: new Date(startMs).toISOString(),
        ends_at: new Date(endMs).toISOString(),
        options: options // backend accepts array of strings
      });
      success('Votación creada exitosamente');
      setShowCreateModal(false);
      setNewPoll({ title: '', description: '', startDate: '', endDate: '', options: ['', ''] });
      await loadPolls();
    } catch (err: any) {
      console.error('Error creating poll:', err);
      // BUG-C (#313): surface API errors (e.g. the 400 'Valid start and end
      // dates...' silent failure) as Spanish messages, never silent.
      const raw = err?.response?.data?.error || err?.response?.data?.message || '';
      const msg = /start must precede end|Valid start and end dates/i.test(raw)
        ? 'Fechas inválidas: la fecha de inicio debe ser anterior a la fecha de cierre'
        : /Title and at least 2 options/i.test(raw)
          ? 'Se requiere un título y al menos 2 opciones'
          : raw
            ? `No se pudo crear la votación: ${raw}`
            : 'Error al crear la votación';
      error(msg);
    }
  };

  const addOption = () => {
    setNewPoll({ ...newPoll, options: [...newPoll.options, ''] });
  };

  const removeOption = (index: number) => {
    if (newPoll.options.length > 2) {
      setNewPoll({ ...newPoll, options: newPoll.options.filter((_, i) => i !== index) });
    }
  };

  const closePoll = async (pollId: string) => {
    try {
      await votingAPI.close(pollId);
      loadPolls();
    } catch (e: any) {
      console.error('Error closing poll:', e);
      alert('No se pudo cerrar la votación');
    }
  };

  const deletePoll = async (pollId: string) => {
    if (!confirm('¿Eliminar esta votación permanentemente?')) return;
    try {
      await votingAPI.delete(pollId);
      loadPolls();
    } catch (e: any) {
      console.error('Error deleting poll:', e);
      alert('No se pudo eliminar la votación');
    }
  };

  // BUG-C (#313): open the create modal with sensible defaults —
  // start = now, end = now + 7 days — in datetime-local format, so the
  // required date contract is pre-filled instead of missing.
  const openCreateModal = () => {
    const fmt = (d: Date) => {
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    setNewPoll({
      title: '',
      description: '',
      startDate: fmt(new Date()),
      endDate: fmt(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)),
      options: ['', '']
    });
    setShowCreateModal(true);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-[var(--v-text-primary)]">Votaciones</h2>
            <p className="text-[var(--v-text-secondary)] mt-1">Participa en las decisiones de tu comunidad</p>
          </div>
        </div>
        <div className="bg-[var(--v-surface-1)] p-12 rounded-xl border border-[var(--v-border-light)] text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-[var(--v-text-secondary)] mt-4">Cargando votaciones...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[var(--v-text-primary)]">Votaciones</h2>
          <p className="text-[var(--v-text-secondary)] mt-1">Participa en las decisiones de tu comunidad</p>
        </div>
        {isAdmin() && (
          <button
            onClick={() => openCreateModal()}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[var(--v-info)] hover:bg-[var(--v-info)] text-white rounded-lg font-medium transition-all hover:shadow-lg hover:shadow-blue-600/25"
          >
            <Plus className="w-5 h-5" />
            Nueva Votación
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-border-light)] shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-[var(--v-info)]" />
            </div>
          </div>
          <p className="text-2xl font-bold text-[var(--v-text-primary)]">{polls.length}</p>
          <p className="text-[var(--v-text-secondary)] text-sm">Total Votaciones</p>
        </div>
        <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-border-light)] shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-[var(--v-text-primary)]">{polls.filter(p => p.status === 'active').length}</p>
          <p className="text-[var(--v-text-secondary)] text-sm">Activas</p>
        </div>
        <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-border-light)] shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-[var(--v-text-primary)]">{polls.reduce((acc, p) => acc + p.totalVotes, 0)}</p>
          <p className="text-[var(--v-text-secondary)] text-sm">Votos Totales</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[var(--v-border-light)]">
        <button
          onClick={() => setActiveTab('active')}
          className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 ${
            activeTab === 'active'
              ? 'text-[var(--v-info)] border-blue-600'
              : 'text-[var(--v-text-secondary)] border-transparent hover:text-[var(--v-text-primary)]'
          }`}
        >
          Activas ({polls.filter(p => p.status === 'active').length})
        </button>
        <button
          onClick={() => setActiveTab('closed')}
          className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 ${
            activeTab === 'closed'
              ? 'text-[var(--v-info)] border-blue-600'
              : 'text-[var(--v-text-secondary)] border-transparent hover:text-[var(--v-text-primary)]'
          }`}
        >
          Cerradas ({polls.filter(p => p.status === 'closed').length})
        </button>
      </div>

      {/* Polls List */}
      <div className="space-y-4">
        {filteredPolls.length === 0 ? (
          <div className="bg-[var(--v-surface-1)] p-12 rounded-xl border border-[var(--v-border-light)] text-center">
            <BarChart3 className="w-16 h-16 text-[var(--v-text-tertiary)] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[var(--v-text-primary)] mb-2">No hay votaciones {activeTab === 'active' ? 'activas' : 'cerradas'}</h3>
            <p className="text-[var(--v-text-secondary)]">Las votaciones aparecerán aquí cuando estén disponibles.</p>
          </div>
        ) : (
          filteredPolls.map((poll) => {
            const maxVotes = Math.max(...poll.options.map(o => o.votes));
            const hasVoted = !!userVotes[poll.id];
            const selectedOptionId = userVotes[poll.id];
            const isChanging = changingVote.has(poll.id);
            // Show voting buttons only for active polls where user hasn't voted
            // OR is in "change vote" mode
            const showVotingButtons = poll.status === 'active' && (!hasVoted || isChanging);
            return (
              <div key={poll.id} className="bg-[var(--v-surface-1)] p-6 rounded-xl border border-[var(--v-border-light)] shadow-sm hover:shadow-md transition-all">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-[var(--v-text-primary)]">{poll.title}</h3>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        poll.status === 'active'
                          ? 'bg-[var(--v-success-bg)] text-[var(--v-success)] border border-emerald-200'
                          : 'bg-[var(--v-base-bg)] text-[var(--v-text-secondary)] border border-[var(--v-border-light)]'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${poll.status === 'active' ? 'bg-emerald-500' : 'bg-slate-400'}`}></span>
                        {poll.status === 'active' ? 'Activa' : 'Cerrada'}
                      </span>
                      {hasVoted && !isChanging && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-[var(--v-info-bg)] text-[var(--v-info)] border border-blue-200">
                          <CheckCircle className="w-3 h-3" />
                          Ya votaste
                        </span>
                      )}
                    </div>
                    <p className="text-[var(--v-text-secondary)] text-sm">{poll.description}</p>
                    <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-[var(--v-text-secondary)]">
                      <span className="flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5" />
                        {poll.totalVotes} votos
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5" />
                        Creada: {poll.createdAt ? new Date(poll.createdAt).toLocaleDateString() : '—'}
                      </span>
                      {poll.endDate && poll.status === 'active' && (
                        <span className="flex items-center gap-1.5 text-[var(--v-warning)]">
                          <Clock className="w-3.5 h-3.5" />
                          Finaliza: {new Date(poll.endDate).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>

                  {isAdmin() && poll.status === 'active' && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setSelectedPoll(poll)}
                        className="p-2 text-[var(--v-text-tertiary)] hover:text-[var(--v-info)] hover:bg-[var(--v-info-bg)] rounded-lg transition-colors"
                        title="Ver detalles"
                      >
                        <BarChart3 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => closePoll(poll.id)}
                        className="p-2 text-[var(--v-text-tertiary)] hover:text-[var(--v-warning)] hover:bg-[var(--v-warning-bg)] rounded-lg transition-colors"
                        title="Cerrar votación"
                      >
                        <AlertCircle className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => deletePoll(poll.id)}
                        className="p-2 text-[var(--v-text-tertiary)] hover:text-[var(--v-danger)] hover:bg-[var(--v-danger-bg)] rounded-lg transition-colors"
                        title="Eliminar votación"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Voting Options — show buttons when user can vote */}
                {showVotingButtons ? (
                  <div className="space-y-2">
                    {poll.options.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => handleVote(poll.id, option.id)}
                        disabled={votingInProgress === poll.id}
                        className="w-full text-left bg-[var(--v-base-bg)] hover:bg-[var(--v-info-bg)] border border-[var(--v-border-light)] hover:border-blue-300 rounded-xl px-4 py-3.5 text-[var(--v-text-primary)] hover:text-[var(--v-info)] font-medium transition-all disabled:opacity-50 disabled:cursor-wait"
                      >
                        {option.text}
                      </button>
                    ))}
                    {isChanging && (
                      <p className="text-xs text-[var(--v-text-tertiary)] mt-2 italic">
                        Selecciona una opción para cambiar tu voto
                      </p>
                    )}
                  </div>
                ) : null}

                {/* Stats — always show, regardless of hasVoted */}
                <div className={`space-y-3 ${showVotingButtons ? 'mt-4 pt-4 border-t border-[var(--v-border-light)]' : ''}`}>
                  {poll.options.map((option) => {
                    const percentage = poll.totalVotes > 0 ? (option.votes / poll.totalVotes) * 100 : 0;
                    const isWinner = option.votes === maxVotes && option.votes > 0;
                    const isSelected = selectedOptionId === option.id;
                    return (
                      <div key={option.id}>
                        <div className="flex justify-between items-center mb-1.5">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium ${isWinner ? 'text-[var(--v-text-primary)]' : 'text-[var(--v-text-secondary)]'}`}>
                              {option.text}
                            </span>
                            {isSelected && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-[var(--v-info)] text-xs rounded-full font-medium">
                                <CheckCircle className="w-3 h-3" />
                                Tu voto
                              </span>
                            )}
                            {isWinner && poll.status === 'closed' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-[var(--v-success)] text-xs rounded-full font-medium">
                                <CheckCircle className="w-3 h-3" />
                                Ganador
                              </span>
                            )}
                          </div>
                          <span className="text-sm text-[var(--v-text-secondary)]">
                            {option.votes} votos ({percentage.toFixed(1)}%)
                          </span>
                        </div>
                        <div className="h-2.5 bg-[var(--v-surface-2)] rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${
                              isSelected ? 'bg-blue-500' : isWinner ? 'bg-emerald-500' : 'bg-blue-300'
                            }`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Change vote button */}
                {poll.status === 'active' && hasVoted && !isChanging && (
                  <div className="mt-4 pt-4 border-t border-[var(--v-border-light)]">
                    <button
                      onClick={() => handleChangeVote(poll.id)}
                      disabled={votingInProgress === poll.id}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[var(--v-info)] hover:bg-[var(--v-info-bg)] rounded-lg transition-colors border border-blue-200 hover:border-blue-300"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Cambiar voto
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--v-surface-1)] rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[var(--v-border-light)] flex items-center justify-between">
              <h3 className="text-xl font-semibold text-[var(--v-text-primary)]">Crear Nueva Votación</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-2 text-[var(--v-text-tertiary)] hover:text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-2)] rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreatePoll} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Título</label>
                <input
                  type="text"
                  value={newPoll.title}
                  onChange={(e) => setNewPoll({ ...newPoll, title: e.target.value })}
                  className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ej: Aprobar Presupuesto Anual"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Descripción</label>
                <textarea
                  value={newPoll.description}
                  onChange={(e) => setNewPoll({ ...newPoll, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Describe el tema a votar..."
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Fecha de inicio</label>
                  <input
                    type="datetime-local"
                    value={newPoll.startDate}
                    onChange={(e) => setNewPoll({ ...newPoll, startDate: e.target.value })}
                    className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Fecha de cierre</label>
                  <input
                    type="datetime-local"
                    value={newPoll.endDate}
                    min={newPoll.startDate || undefined}
                    onChange={(e) => setNewPoll({ ...newPoll, endDate: e.target.value })}
                    className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Opciones</label>
                <div className="space-y-2">
                  {newPoll.options.map((option, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => {
                          const newOptions = [...newPoll.options];
                          newOptions[index] = e.target.value;
                          setNewPoll({ ...newPoll, options: newOptions });
                        }}
                        placeholder={`Opción ${index + 1}`}
                        className="flex-1 px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                      {newPoll.options.length > 2 && (
                        <button
                          type="button"
                          onClick={() => removeOption(index)}
                          className="p-2.5 text-[var(--v-text-tertiary)] hover:text-[var(--v-danger)] hover:bg-[var(--v-danger-bg)] rounded-lg transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addOption}
                  className="mt-2 text-[var(--v-info)] text-sm font-medium hover:text-[var(--v-info)] flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" /> Agregar opción
                </button>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2.5 border border-[var(--v-border-light)] text-[var(--v-text-primary)] rounded-lg font-medium hover:bg-[var(--v-base-bg)] transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 bg-[var(--v-info)] hover:bg-[var(--v-info)] text-white rounded-lg font-medium transition-colors"
                >
                  Crear Votación
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
